import { useState, useEffect } from "react";
import { api, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sparkles, TrendingUp, Zap, ShieldCheck, AlertTriangle, ChevronDown,
  Wand2, Loader2, FileSignature,
} from "lucide-react";
import { toast } from "sonner";
import { US_STATES } from "@/lib/usStates";

const TIER_STYLES = {
  quick: {
    icon: Zap,
    ring: "border-emerald-300",
    bg: "bg-emerald-50",
    text: "text-emerald-900",
    accent: "text-emerald-700",
    chip: "bg-emerald-100 text-emerald-800 border-emerald-300",
    btn: "bg-emerald-600 hover:bg-emerald-700 text-white",
  },
  recommended: {
    icon: TrendingUp,
    ring: "border-violet-400 ring-2 ring-violet-200",
    bg: "bg-violet-50",
    text: "text-violet-900",
    accent: "text-violet-700",
    chip: "bg-violet-100 text-violet-800 border-violet-300",
    btn: "bg-violet-600 hover:bg-violet-700 text-white",
  },
  max_safe: {
    icon: ShieldCheck,
    ring: "border-amber-300",
    bg: "bg-amber-50",
    text: "text-amber-900",
    accent: "text-amber-700",
    chip: "bg-amber-100 text-amber-800 border-amber-300",
    btn: "bg-amber-600 hover:bg-amber-700 text-white",
  },
};

const FLAG_COPY = {
  "short-sale-risk":            "Mortgage balance exceeds even the Maximum Safe Offer — this deal is a short-sale (or dead) unless the seller has cash to bring to closing.",
  "mortgage-above-recommended": "Mortgage balance exceeds the Recommended Offer — start negotiations at Maximum Safe or push for a subject-to.",
  "low-equity":                 "Owner equity is below 20% — expect resistance to any wholesale-range offer.",
  "heavy-rehab":                "Rehab budget exceeds 35% of ARV — verify repair estimate before committing.",
};

const CONDITION_PRESETS = [
  { key: "",         label: "— Custom / manual —", rate: null },
  { key: "cosmetic", label: "Light Cosmetics",      rate: 15 },
  { key: "medium",   label: "Moderate",              rate: 25 },
  { key: "heavy",    label: "Heavy",                 rate: 40 },
];

const DEFAULT_VALUES = {
  arv: "",
  living_area_sqft: "",
  condition_key: "cosmetic",   // default to Light Cosmetics (admin 2026-02-17)
  repair_cost_per_sqft: 15,    // matches cosmetic preset — $15/sqft
  repairs_override: "",
  days_on_market: 0,
  equity_pct: 100,
  mortgage_balance: 0,
  motivation_score: 50,
  investor_demand: "med",
  zip_avg_assignment_fee: 15000,
};

export default function SmartOfferRecommender({
  propertyId = null,
  onOfferCreated = null,
  onTiersComputed = null,
}) {
  const [values, setValues] = useState(DEFAULT_VALUES);
  const [zip, setZip] = useState("");
  const [zipFeeSource, setZipFeeSource] = useState("default"); // "user_deals" | "default"
  const [zipFeeSampleSize, setZipFeeSampleSize] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showMath, setShowMath] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);
  const [creatingTier, setCreatingTier] = useState(null);
  const [terms, setTerms] = useState({
    purchase_price: 0, override_state: "", override_inspection_days: 15,
    closing_date: "", earnest_money: 100, buyer_email: "",
  });
  const [signDialog, setSignDialog] = useState(null); // { offer, tier }
  const [sellerEmail, setSellerEmail] = useState("");
  const [allEmails, setAllEmails] = useState([]);      // every email pulled from lead + skip-trace cache
  const [sellerName, setSellerName] = useState("");
  const [sending, setSending] = useState(false);

  const setV = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  // ---- 1) Auto-fill from Property (Motivation Score Auto lives here too) ----
  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/properties/${propertyId}/smart-offer-inputs`);
        if (cancelled) return;
        setValues({
          arv:                    data.arv || "",
          living_area_sqft:       data.living_area_sqft || "",
          repair_cost_per_sqft:   data.repair_cost_per_sqft || 25,
          repairs_override:       "",
          days_on_market:         data.days_on_market || 0,
          equity_pct:             data.equity_pct ?? 100,
          mortgage_balance:       data.mortgage_balance || 0,
          motivation_score:       data.motivation_score ?? 50,
          investor_demand:        data.investor_demand || "med",
          zip_avg_assignment_fee: data.zip_avg_assignment_fee || 15000,
        });
        setZip(data.zip || "");
        setAutoFilled(true);
        toast.success("Filled from property data");
      } catch (_) { /* silent — user can enter manually */ }
    })();
    return () => { cancelled = true; };
  }, [propertyId]);

  // ---- 2) ZIP-fee learning: whenever ZIP changes, sharpen the fee ----
  useEffect(() => {
    const cleanZip = (zip || "").trim();
    if (cleanZip.length !== 5) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/offers/zip-avg-fee?zip=${cleanZip}`);
        if (cancelled) return;
        setZipFeeSource(data.source);
        setZipFeeSampleSize(data.sample_size || 0);
        // Only auto-apply the learned fee if the user hasn't already touched it
        // to something non-default.
        if (data.source === "user_deals") {
          setV("zip_avg_assignment_fee", data.avg_fee);
        }
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [zip]);

  const compute = async () => {
    if (!values.arv || Number(values.arv) < 1) {
      toast.error("Enter an ARV to compute offers");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/offers/smart-recommend", {
        arv:                    Number(values.arv),
        living_area_sqft:       Number(values.living_area_sqft) || 0,
        repair_cost_per_sqft:   Number(values.repair_cost_per_sqft) || 25,
        condition_key:          values.condition_key || null,
        repairs_override:       values.repairs_override
                                  ? Number(values.repairs_override) : null,
        days_on_market:         Number(values.days_on_market) || 0,
        equity_pct:             Number(values.equity_pct) || 100,
        mortgage_balance:       Number(values.mortgage_balance) || 0,
        motivation_score:       Number(values.motivation_score) || 50,
        investor_demand:        values.investor_demand,
        zip_avg_assignment_fee: Number(values.zip_avg_assignment_fee) || 15000,
        property_id:            propertyId,
      });
      setResult(data);
      // Bubble the 3 tiers up so parent screens (Property Lookup) can wire
      // an "Add to Lead" chip row without re-hitting the API.
      if (onTiersComputed && Array.isArray(data?.tiers)) {
        onTiersComputed(data.tiers);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recommendation failed");
    } finally { setLoading(false); }
  };

  // ---- 4) Save & Send: create an offer at a specific tier's price ----
  const createOfferAtTier = async (tier) => {
    if (!propertyId) {
      toast.error("Look up a property first to send an offer");
      return;
    }
    setCreatingTier(tier.key);
    try {
      const { data: offer } = await api.post("/offers", {
        property_id:      propertyId,
        purchase_price:   Number(tier.price),
        earnest_money:    100,
        override_inspection_days: 15,
        override_state:   values.state || undefined,
        tier_key:         tier.key,   // Tier History — power the analytics chart
      });
      toast.success(`Offer created at ${money(tier.price)}`);
      setTerms({
        purchase_price: Number(tier.price), override_state: values.state || "",
        override_inspection_days: 15, closing_date: "", earnest_money: 100,
        buyer_email: "",
      });

      // Auto-Send for Signature — resolve seller email from any lead tied to
      // this property, plus every email in the skip-trace cache, so the user
      // can pick which one to send to.
      let email = "", name = "", emails = [];
      try {
        const { data: se } = await api.get(`/properties/${propertyId}/seller-email`);
        email  = se.seller_email || "";
        name   = se.seller_name  || "";
        emails = se.all_emails   || [];
      } catch (_) { /* fall through */ }

      setSellerEmail(email);
      setSellerName(name);
      setAllEmails(emails);
      setSignDialog({ offer, tier });

      if (onOfferCreated) onOfferCreated(offer, tier);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Offer create failed");
    } finally { setCreatingTier(null); }
  };

  const sendForSignature = async () => {
    if (!signDialog?.offer?.id) return;
    if (!sellerEmail || !sellerEmail.includes("@")) {
      toast.error("Enter a valid seller email");
      return;
    }
    setSending(true);
    try {
      // Apply any manual edits to the contract terms before it's sent.
      const _patch = {};
      if (terms.purchase_price) _patch.purchase_price = Number(terms.purchase_price);
      if (terms.override_state) _patch.override_state = terms.override_state;
      if (terms.override_inspection_days) _patch.override_inspection_days = Number(terms.override_inspection_days);
      if (terms.closing_date) _patch.closing_date = terms.closing_date;
      if (terms.earnest_money) _patch.earnest_money = Number(terms.earnest_money);
      if (terms.buyer_email) _patch.buyer_email = terms.buyer_email;
      if (Object.keys(_patch).length) {
        try { await api.patch(`/offers/${signDialog.offer.id}`, _patch); } catch (_) { /* non-fatal */ }
      }
      await api.post(`/offers/${signDialog.offer.id}/send-for-signature`, {
        seller_email: sellerEmail,
        test_mode:    false,   // always LIVE — real email + real signature
      });
      toast.success(`Offer sent to ${sellerEmail} for signature`);
      setSignDialog(null);
      setSellerEmail("");
      setSellerName("");
      setAllEmails([]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Send failed";
      if (msg.includes("not connected")) {
        toast.error("Connect SignWell in Settings first");
      } else {
        toast.error(msg);
      }
    } finally { setSending(false); }
  };

  return (
    <div className="card-flat p-5 md:p-6" data-testid="smart-offer-recommender">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0">
          <Sparkles size={18} strokeWidth={2.2} />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-2xl md:text-3xl leading-tight">
            Smart Offer Recommender
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Three offer tiers computed from ARV, repairs, market signals, and
            ZIP-level assignment fee — so you always know your Quick,
            Recommended, and ceiling number before you dial.
          </p>
          {autoFilled && (
            <div
              data-testid="smart-autofilled-banner"
              className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1"
            >
              <Wand2 size={11} strokeWidth={2.4} />
              Auto-filled from this property
            </div>
          )}
        </div>
      </div>

      {/* ============ INPUTS ============ */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
        <Field label="ARV (After-Repair Value)" required>
          <MoneyInput value={values.arv} onChange={(v) => setV("arv", v)}
                      testid="smart-arv" />
        </Field>
        <Field label="Living area (sqft)">
          <Input type="number" min={0} value={values.living_area_sqft}
                 onChange={(e) => setV("living_area_sqft", e.target.value)}
                 data-testid="smart-sqft" placeholder="e.g. 1400" />
        </Field>
        <Field label="Property condition"
               hint="Preset repair rate. Pick 'Custom' to enter your own $/sqft.">
          <select
            value={values.condition_key}
            onChange={(e) => {
              const key = e.target.value;
              const preset = CONDITION_PRESETS.find((p) => p.key === key);
              setValues((s) => ({
                ...s,
                condition_key: key,
                repair_cost_per_sqft: preset && preset.rate != null
                  ? preset.rate
                  : s.repair_cost_per_sqft,
              }));
            }}
            data-testid="smart-condition"
            className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm"
          >
            {CONDITION_PRESETS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}{p.rate != null ? `  ·  $${p.rate}/sqft` : ""}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Repair cost / sqft"
               hint={values.condition_key
                 ? `Preset from "${(CONDITION_PRESETS.find((p) => p.key === values.condition_key) || {}).label}" — edit to override.`
                 : "Default $25/sqft. Bump up for gut rehabs."}>
          <MoneyInput value={values.repair_cost_per_sqft}
                      onChange={(v) => setValues((s) => ({ ...s, repair_cost_per_sqft: v, condition_key: "" }))}
                      testid="smart-repair-rate" />
        </Field>
        <Field label="Repairs override (optional)"
               hint="Enter a firm number to skip sqft × rate.">
          <MoneyInput value={values.repairs_override}
                      onChange={(v) => setV("repairs_override", v)}
                      testid="smart-repair-override" />
        </Field>
        <Field label="Days on market">
          <Input type="number" min={0} value={values.days_on_market}
                 onChange={(e) => setV("days_on_market", e.target.value)}
                 data-testid="smart-dom" placeholder="0" />
        </Field>
        <Field
          label="Deal costs (fixed)"
          hint="Wholesale $20,000 · Closing 7% of ARV · Holding 5% of ARV — applied to every tier"
        >
          <div data-testid="smart-fixed-costs"
               className="h-10 flex items-center px-3 rounded-lg border border-[var(--app-line)] bg-[var(--app-surface)] text-sm text-muted-foreground">
            $20,000 wholesale · not editable
          </div>
        </Field>
        <Field label="Property state" hint="Nationwide — sets the contract's state clauses">
          <select
            value={values.state || ""}
            onChange={(e) => setV("state", e.target.value)}
            data-testid="smart-state"
            className="h-10 w-full rounded-lg border border-[var(--app-line)] bg-white px-3 text-sm"
          >
            <option value="">— select state —</option>
            {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Mortgage balance (if known)">
          <MoneyInput value={values.mortgage_balance}
                      onChange={(v) => setV("mortgage_balance", v)}
                      testid="smart-mortgage" />
        </Field>
        <Field label="Owner equity %">
          <Input type="number" min={0} max={100} value={values.equity_pct}
                 onChange={(e) => setV("equity_pct", e.target.value)}
                 data-testid="smart-equity" />
        </Field>
        <Field label="Local investor demand">
          <select
            value={values.investor_demand}
            onChange={(e) => setV("investor_demand", e.target.value)}
            data-testid="smart-demand"
            className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm"
          >
            <option value="low">Low</option>
            <option value="med">Medium</option>
            <option value="high">High</option>
          </select>
        </Field>
        <div className="md:col-span-3">
          <Field
            label={`Seller motivation score · ${values.motivation_score}/100`}
            hint={
              autoFilled
                ? "Auto-pulled from this property's AI motivation score — override with the slider if the seller told you something new"
                : "0 = won't budge · 50 = neutral · 100 = must-sell-now"
            }
          >
            <div className="flex items-center gap-3 pt-1">
              <span className="text-[10px] text-muted-foreground w-8">Cold</span>
              <Slider
                min={0} max={100} step={1}
                value={[values.motivation_score]}
                onValueChange={(v) => setV("motivation_score", v[0])}
                data-testid="smart-motivation"
                className="flex-1"
              />
              <span className="text-[10px] text-muted-foreground w-8 text-right">Hot</span>
            </div>
          </Field>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Button
          onClick={compute}
          disabled={loading}
          data-testid="smart-compute-btn"
          className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white"
        >
          <Sparkles size={14} className="mr-1.5" />
          {loading ? "Calculating…" : (result ? "Recompute" : "Compute 3 offers")}
        </Button>
        {result && (
          <button
            onClick={() => setShowMath((s) => !s)}
            data-testid="smart-toggle-math"
            className="text-xs uppercase tracking-widest text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
          >
            <ChevronDown size={12} className={`transition-transform ${showMath ? "rotate-180" : ""}`} />
            {showMath ? "Hide math" : "Show math"}
          </button>
        )}
      </div>

      {/* ============ RESULTS ============ */}
      {result && (
        <>
          {result.flags?.length > 0 && (
            <div className="mt-5 space-y-2" data-testid="smart-flags">
              {result.flags.map((f) => (
                <div
                  key={f}
                  className="flex items-start gap-2 rounded-xl bg-rose-50 border border-rose-200 text-rose-900 text-xs p-3"
                >
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <div>{FLAG_COPY[f] || f}</div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="smart-tiers">
            {result.tiers.map((t) => {
              const s = TIER_STYLES[t.key] || TIER_STYLES.recommended;
              const Icon = s.icon;
              const isCreating = creatingTier === t.key;
              return (
                <div
                  key={t.key}
                  data-testid={`smart-tier-${t.key}`}
                  className={`rounded-2xl border ${s.ring} ${s.bg} p-5 flex flex-col`}
                >
                  <div className="flex items-center justify-between">
                    <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest ${s.accent}`}>
                      <Icon size={12} strokeWidth={2.4} />
                      {t.label}
                    </div>
                    <div className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${s.chip}`}>
                      {t.arv_pct}% ARV
                    </div>
                  </div>
                  <div className={`font-display font-bold text-3xl md:text-4xl mt-2 ${s.text}`}>
                    {money(t.price)}
                  </div>
                  <div className={`text-xs font-medium mt-1 ${s.text}`}>
                    {t.headline}
                  </div>
                  <div className="text-[11px] text-slate-600 mt-1 leading-snug">
                    {t.subtitle}
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-200/70 flex items-center justify-between text-[11px]">
                    <span className="text-slate-500">Profit room</span>
                    <span className={`font-mono font-semibold ${s.text}`}>
                      {money(t.profit_room)}
                    </span>
                  </div>
                  {showMath && (
                    <ul className="mt-3 space-y-1.5 text-[11px] text-slate-600">
                      {t.notes.map((n, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <span className="mt-1 w-1 h-1 rounded-full bg-slate-400 shrink-0" />
                          <span>{n}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Send this tier — only when a property is loaded */}
                  <button
                    onClick={() => createOfferAtTier(t)}
                    disabled={!propertyId || isCreating}
                    data-testid={`smart-send-tier-${t.key}`}
                    title={
                      propertyId
                        ? `Generate offer at ${money(t.price)}`
                        : "Look up a property first to send this offer"
                    }
                    className={`mt-4 inline-flex items-center justify-center gap-1.5 rounded-sm px-3 py-2 text-xs uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${s.btn}`}
                  >
                    {isCreating ? (
                      <><Loader2 size={12} className="animate-spin" /> Creating…</>
                    ) : (
                      <><FileSignature size={12} strokeWidth={2.4} /> Send this offer</>
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {showMath && (
            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4 text-[11px] text-slate-700 font-mono">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-y-1.5 gap-x-4">
                <span>ARV</span>              <span className="text-right">{money(result.arv)}</span>
                <span>Repairs</span>          <span className="text-right">{money(result.repairs)}</span>
                <span>Sqft × $/sqft</span>    <span className="text-right">{result.living_area_sqft} × ${result.repair_cost_per_sqft}</span>
                <span>Wholesale fee</span>    <span className="text-right">{money(result.wholesale_fee || result.zip_avg_assignment_fee)}</span>
                <span>Fee rule</span>         <span className="text-right">{result.wholesale_fee_rule || "—"}</span>
                <span>Motivation</span>       <span className="text-right">{result.motivation_score}/100</span>
                <span>Days on market</span>   <span className="text-right">{result.days_on_market}d</span>
                <span>Investor demand</span>  <span className="text-right">{result.investor_demand}</span>
                <span>Recommended adj</span>  <span className="text-right">{result.recommend_adjustment_pct > 0 ? "+" : ""}{result.recommend_adjustment_pct}%</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Auto-Send Signature Dialog — opens right after an offer is created */}
      <Dialog open={!!signDialog}
              onOpenChange={(o) => { if (!o) setSignDialog(null); }}>
        <DialogContent className="max-w-lg" data-testid="auto-send-sign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature size={16} className="text-violet-700" />
              Send offer for signature
            </DialogTitle>
            <DialogDescription>
              {signDialog?.tier && (
                <>
                  <b>{signDialog.tier.label}</b> · {money(signDialog.tier.price)} ·
                  {" "}{signDialog.tier.arv_pct}% ARV
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {sellerName && (
              <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-2 inline-flex items-center gap-2"
                   data-testid="auto-send-sign-lead-badge">
                <Wand2 size={12} strokeWidth={2.4} />
                Prefilled from lead: <b>{sellerName}</b>
              </div>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-widest">
                Seller email
              </Label>
              {/* If we found multiple emails in the lead+cache, show a
                  dropdown to pick — otherwise a plain input */}
              {allEmails.length > 1 && (
                <select
                  value={sellerEmail}
                  onChange={(e) => setSellerEmail(e.target.value)}
                  data-testid="auto-send-sign-email-select"
                  className="mt-1 h-10 w-full rounded-sm border border-input bg-background px-3 text-sm"
                >
                  {allEmails.map((em) => (
                    <option key={em} value={em}>{em}</option>
                  ))}
                  <option value="__custom__">✎ Enter a different email…</option>
                </select>
              )}
              {(allEmails.length <= 1 || sellerEmail === "__custom__") && (
                <Input
                  type="email"
                  value={sellerEmail === "__custom__" ? "" : sellerEmail}
                  onChange={(e) => setSellerEmail(e.target.value)}
                  data-testid="auto-send-sign-email"
                  placeholder="seller@example.com"
                  className="mt-1"
                  autoFocus={sellerEmail === "__custom__"}
                />
              )}
              <div className="text-[10px] text-muted-foreground mt-1">
                {allEmails.length > 1
                  ? `${allEmails.length} known emails on file — pick one or enter a custom address.`
                  : sellerEmail
                  ? "Contract PDF will be emailed to this address for signature."
                  : "No email on the lead yet — enter one to send."}
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-[var(--app-line)] p-3 bg-[var(--app-surface)]"
               data-testid="contract-terms-editor">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              Contract terms — edit before sending
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px]">Purchase price</Label>
                <Input type="number" value={terms.purchase_price}
                       onChange={(e) => setTerms({ ...terms, purchase_price: e.target.value })}
                       data-testid="terms-price" className="mt-0.5" />
              </div>
              <div>
                <Label className="text-[11px]">State</Label>
                <select value={terms.override_state}
                        onChange={(e) => setTerms({ ...terms, override_state: e.target.value })}
                        data-testid="terms-state"
                        className="mt-0.5 h-10 w-full rounded-md border border-[var(--app-line)] bg-white px-2 text-sm">
                  <option value="">— state —</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[11px]">Inspection (days)</Label>
                <Input type="number" value={terms.override_inspection_days}
                       onChange={(e) => setTerms({ ...terms, override_inspection_days: e.target.value })}
                       data-testid="terms-inspection" className="mt-0.5" />
              </div>
              <div>
                <Label className="text-[11px]">Closing date</Label>
                <Input type="date" value={terms.closing_date}
                       onChange={(e) => setTerms({ ...terms, closing_date: e.target.value })}
                       data-testid="terms-closing" className="mt-0.5" />
              </div>
              <div className="col-span-2">
                <Label className="text-[11px]">Buyer signer email (optional — adds 2nd signature)</Label>
                <Input type="email" value={terms.buyer_email}
                       placeholder="you@yourcompany.com"
                       onChange={(e) => setTerms({ ...terms, buyer_email: e.target.value })}
                       data-testid="terms-buyer-email" className="mt-0.5" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSignDialog(null)}
              data-testid="auto-send-sign-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={sendForSignature}
              disabled={sending || !sellerEmail || sellerEmail === "__custom__"}
              data-testid="auto-send-sign-send"
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              {sending ? (
                <><Loader2 size={13} className="animate-spin mr-1" /> Sending…</>
              ) : (
                <><FileSignature size={13} className="mr-1" />
                  Send for signature
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-slate-600">
        {label}{required && <span className="text-rose-500 ml-1">*</span>}
      </Label>
      <div className="mt-1">{children}</div>
      {hint && (
        <div className="text-[10px] text-muted-foreground mt-1 leading-snug">
          {hint}
        </div>
      )}
    </div>
  );
}

function MoneyInput({ value, onChange, testid }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
      <Input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="pl-6"
        placeholder="0"
      />
    </div>
  );
}
